import { useState, useEffect, useRef } from 'react';
import { Grid } from '@mui/material';
import VuiAvatar from 'components/VuiAvatar';
import placeholderImg from 'assets/placeholder.png';
import * as S from './style';
import { useCustomDebounce, parseMentionRawToDisplay, parseMentionDisplayToRaw } from 'utils/utils';

export const MentionInput = ({
  value = '',
  onChange,
  onMentionedUsersChange,
  placeholder = 'Escrever um comentário... Use @ para mencionar alguém',
  multiline = true,
  onUserSearch,
  options = [],
  ...inputProps
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const [mentionedUsers, setMentionedUsers] = useState([]);
  const [showMentions, setShowMentions] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const inputRef = useRef(null);

  const debouncedUserSearch = useCustomDebounce((searchTerm) => {
    if (onUserSearch) {
      onUserSearch(searchTerm);
    }
  }, 400);

  const userTypeTranslation = {
    ADMINISTRATOR: "Administrador",
    H_COMERCIAL: "Head Comercial",
    COMERCIAL: "Comercial",
    H_MARKETING: "Head de Marketing",
    MARKETING: "Marketing",
  };

  const extractMentionedUsers = (rawText) => {
    const matches = rawText.match(/#\[([^\]]+)\(([^\)]+)\)\]/g);
    if (!matches) return [];
    return matches.map(match => {
      const nameMatch = match.match(/#\[([^\]]+)\(/);
      const idMatch = match.match(/\(([^\)]+)\)/);
      return {
        name: nameMatch ? nameMatch[1] : null,
        id: idMatch ? idMatch[1] : null
      };
    }).filter(user => user.name && user.id);
  };

  useEffect(() => {
    if (value !== parseMentionDisplayToRaw(displayValue, mentionedUsers)) {
      setDisplayValue(parseMentionRawToDisplay(value));
      setMentionedUsers(extractMentionedUsers(value));
    }
  }, [value]);

  useEffect(() => {
    if (onMentionedUsersChange) {
      onMentionedUsersChange(mentionedUsers);
    }
  }, [mentionedUsers, onMentionedUsersChange]);

  const handleInputChange = (e) => {
    const displayValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    setCursorPosition(cursorPos);

    if (e.nativeEvent.inputType === 'deleteContentBackward') {
      const mentions = [];
      
      mentionedUsers.forEach(user => {
        const mentionText = `@${user.name}`;
        let searchStart = 0;
        let foundIndex;
        
        while ((foundIndex = displayValue.indexOf(mentionText, searchStart)) !== -1) {
          mentions.push({
            start: foundIndex,
            end: foundIndex + mentionText.length,
            name: user.name,
            userId: user.id,
            fullMatch: mentionText
          });
          searchStart = foundIndex + 1;
        }
      });

      for (const mention of mentions) {
        if (cursorPos >= mention.start + 1 && cursorPos <= mention.end) {
          e.preventDefault();
          
          const newDisplayValue = displayValue.slice(0, mention.start) + displayValue.slice(mention.end);
          setDisplayValue(newDisplayValue);

          const updatedMentionedUsers = mentionedUsers.filter(user => user.id !== mention.userId);
          setMentionedUsers(updatedMentionedUsers);
          
          const newRawValue = parseMentionDisplayToRaw(newDisplayValue, updatedMentionedUsers);
          if (onChange) onChange(newRawValue);

          setTimeout(() => {
            if (inputRef.current) {
              inputRef.current.setSelectionRange(mention.start, mention.start);
            }
          }, 0);
          
          return;
        }
      }
    }

    const removedMentions = mentionedUsers.filter(user => {
      const mention = `@${user.name}`;
      return !displayValue.includes(mention);
    });

    let updatedMentionedUsers = mentionedUsers;
    if (removedMentions.length > 0) {
      updatedMentionedUsers = mentionedUsers.filter(user =>
        !removedMentions.some(removed => removed.id === user.id)
      );
      setMentionedUsers(updatedMentionedUsers);
    }

    setDisplayValue(displayValue);
    const newRawValue = parseMentionDisplayToRaw(displayValue, updatedMentionedUsers);
    if (onChange) onChange(newRawValue);

    const lastAtSymbol = displayValue.lastIndexOf('@', cursorPos);
    if (lastAtSymbol !== -1) {
      const nextSpace = displayValue.indexOf(' ', lastAtSymbol);
      const searchEnd = nextSpace === -1 ? displayValue.length : nextSpace;
      const searchStr = displayValue.slice(lastAtSymbol + 1, searchEnd);

      if (cursorPos > lastAtSymbol && cursorPos <= searchEnd) {
        const isExistingMention = mentionedUsers.some(user => {
          const mentionText = `@${user.name}`;
          const mentionStart = displayValue.indexOf(mentionText, Math.max(0, lastAtSymbol - mentionText.length));
          return mentionStart !== -1 && 
                 mentionStart === lastAtSymbol && 
                 lastAtSymbol + mentionText.length <= displayValue.length &&
                 displayValue.substr(lastAtSymbol, mentionText.length) === mentionText;
        });
        
        if (!isExistingMention) {
          setShowMentions(true);
          debouncedUserSearch(searchStr);
        } else {
          setShowMentions(false);
        }
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
  };



  const insertMention = (user) => {
    const beforeMention = displayValue.slice(0, displayValue.lastIndexOf('@'));
    const afterMention = displayValue.slice(cursorPosition);
    const newDisplayValue = `${beforeMention}@${user.name} ${afterMention}`;
    
    const updatedMentionedUsers = [...mentionedUsers, { id: user.id, name: user.name }];
    setMentionedUsers(updatedMentionedUsers);
    
    setDisplayValue(newDisplayValue);
    const newRawValue = parseMentionDisplayToRaw(newDisplayValue, updatedMentionedUsers);
    if (onChange) onChange(newRawValue);
    setShowMentions(false);

    if (inputRef.current) {
      const newCursorPos = beforeMention.length + user.name.length + 2;
      inputRef.current.focus();
      setTimeout(() => {
        inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }
  };

  return (
    <S.MentionContainer>
      <S.Input
        {...inputProps}
        multiline={multiline}
        placeholder={placeholder}
        inputRef={inputRef}
        onChange={handleInputChange}
        value={displayValue}
        fullWidth
      />
      {showMentions && options?.length > 0 && (
        <S.MentionMenu>
          {options.map((group) => (
            <div key={group.role} style={{ width: '100%' }}>
              <S.MentionGroupTitle>
                {userTypeTranslation[group.role]}
              </S.MentionGroupTitle>
              <div style={{ width: '100%' }}>
                {group.users.map((user) => (
                  <S.MentionItem
                    key={user.id}
                    onClick={() => insertMention(user)}
                  >
                    <VuiAvatar src={user.avatar ?? placeholderImg} size="sm" />
                    <Grid>
                      <S.MentionName>{user.name}</S.MentionName>
                    </Grid>
                  </S.MentionItem>
                ))}
              </div>
            </div>
          ))}
        </S.MentionMenu>
      )}
    </S.MentionContainer>
  );
};